import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';

import { Icd10BlockGroup, Icd10DictionaryEntry, Icd10NamesLoadState, Icd10TreeAction, icd10NodeEntry, isIcd10CodeUnder } from '@lib/insurance-packages/data-access';
import { TranslatePipe } from '@ngx-translate/core';

import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';

import { DiagnosesCountBadgeComponent } from '../diagnoses-count-badge/diagnoses-count-badge.component';
import { Icd10CodeRowComponent } from '../icd10-code-row/icd10-code-row.component';

/**
 * Block → category → code rows of an ICD-10 code list (see `groupIcd10Codes`). Presentational: names and per-node
 * load states come in, and opening a node or retrying a failed one is reported out so the host can fetch names.
 *
 * An `action` adds, on every row, a checkbox and a remove or add button; once anything is selected the buttons give
 * way to the host's bulk action. Removing or selecting a block or category takes all its codes. Adding a node reports
 * its dictionary entry — the host adds everything under it — and codes already listed (`presentCodes`) are marked.
 * No action by default — read-only.
 */
@Component({
  selector: 'insurance-packages-icd10-diagnoses-tree',
  standalone: true,
  imports: [NgTemplateOutlet, MatIcon, MatProgressSpinner, MatTooltip, TranslatePipe, DiagnosesCountBadgeComponent, Icd10CodeRowComponent],
  templateUrl: './icd10-diagnoses-tree.component.html',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Icd10DiagnosesTreeComponent {
  readonly groups = input.required<Icd10BlockGroup[]>();
  /** Display names keyed by code — blocks, categories and codes alike. */
  readonly names = input<Record<string, string>>({});
  /** Names lookup state keyed by block key or category. */
  readonly loadStates = input<Record<string, Icd10NamesLoadState>>({});
  /** Whether loading the block list failed; the tree then falls back to letter groups. */
  readonly groupsError = input<boolean>(false);
  readonly action = input<Icd10TreeAction | null>(null);
  /** Codes ticked for a bulk action. */
  readonly selectedCodes = input<ReadonlySet<string>>(new Set());
  /** Codes already in the list — marked, and not selectable, while adding. */
  readonly presentCodes = input<ReadonlySet<string>>(new Set());

  readonly blockOpened = output<Icd10BlockGroup>();
  readonly categoryOpened = output<string>();
  readonly retry = output<string>();
  readonly retryGroups = output<void>();
  /** Codes to remove: one code, or every code of a block or category. */
  readonly remove = output<string[]>();
  /** The dictionary entry of a block, category or code to add, with everything under it. */
  readonly add = output<Icd10DictionaryEntry>();
  readonly selectedCodesChange = output<ReadonlySet<string>>();

  /** Row buttons are offered only while nothing is selected — a selection is acted on in bulk instead. */
  protected readonly showAction = computed(() => !!this.action() && this.selectedCodes().size === 0);

  /** A node's entry when it can be added as a whole; letter groups stand for no single entry. */
  protected nodeEntry(key: string): Icd10DictionaryEntry | null {
    return icd10NodeEntry(key);
  }

  /** How many listed codes sit under a node — shown while adding. */
  protected presentUnder(key: string): number {
    const entry = icd10NodeEntry(key);
    return entry ? [...this.presentCodes()].filter(code => isIcd10CodeUnder(code, entry)).length : 0;
  }

  private readonly expandedBlocks = signal<ReadonlySet<string>>(new Set());
  private readonly expandedCategories = signal<ReadonlySet<string>>(new Set());

  protected isBlockExpanded(key: string): boolean {
    return this.expandedBlocks().has(key);
  }

  protected toggleBlock(group: Icd10BlockGroup): void {
    this.expandedBlocks.update(blocks => toggled(blocks, group.key));
    if (this.isBlockExpanded(group.key)) this.blockOpened.emit(group);
  }

  protected isCategoryExpanded(category: string): boolean {
    return this.expandedCategories().has(category);
  }

  protected blockCodes(group: Icd10BlockGroup): string[] {
    return group.categories.flatMap(item => item.codes);
  }

  /** The codes a checkbox acts on: all of them, except — while adding — those already listed. */
  protected selectable(codes: string[]): string[] {
    return this.action() === 'add' ? codes.filter(code => !this.presentCodes().has(code)) : codes;
  }

  protected selection(codes: string[]): 'all' | 'some' | 'none' {
    const candidates = this.selectable(codes);
    const selected = candidates.filter(code => this.selectedCodes().has(code)).length;
    if (!selected) return 'none';
    return selected === candidates.length ? 'all' : 'some';
  }

  /** Ticks every selectable code given, or clears them all when they are all ticked already. */
  protected toggleSelection(codes: string[]): void {
    const next = new Set(this.selectedCodes());
    const select = this.selection(codes) !== 'all';
    for (const code of this.selectable(codes)) {
      if (select) next.add(code);
      else next.delete(code);
    }
    this.selectedCodesChange.emit(next);
  }

  /**
   * Ticks or clears a block's or category's codes. Ticking also opens the node and every category under it, so the
   * exact diagnoses a group stands for are on screen before they are acted on.
   */
  protected toggleNodeSelection(codes: string[], block?: Icd10BlockGroup, category?: string): void {
    const selecting = this.selection(codes) !== 'all';
    this.toggleSelection(codes);
    if (!selecting) return;

    if (block) this.revealBlock(block);
    else if (category) this.revealCategory(category);
  }

  protected toggleCategory(category: string): void {
    this.expandedCategories.update(categories => toggled(categories, category));
    if (this.isCategoryExpanded(category)) this.categoryOpened.emit(category);
  }

  private revealBlock(group: Icd10BlockGroup): void {
    if (!this.isBlockExpanded(group.key)) {
      this.expandedBlocks.update(blocks => toggled(blocks, group.key));
      this.blockOpened.emit(group);
    }
    for (const item of group.categories) this.revealCategory(item.category);
  }

  private revealCategory(category: string): void {
    if (this.isCategoryExpanded(category)) return;
    this.expandedCategories.update(categories => toggled(categories, category));
    this.categoryOpened.emit(category);
  }
}

function toggled(set: ReadonlySet<string>, value: string): ReadonlySet<string> {
  const next = new Set(set);
  if (!next.delete(value)) next.add(value);
  return next;
}
